import React, { useEffect, useState, useRef } from 'react';
import { Body1, H3, Body2, InputWithBorder, Modal } from 'ensightful-koala-ui';
import styled from 'styled-components';
import uuid from 'uuid';

import { RadioButton } from 'components/molecules/Button';
import { CourseSetUpContainer, HeaderContainer } from 'components/organisms/CourseSetUp';
import SmartGroupFormation from 'components/organisms/SmartGroupFormation';
import { Button, Text, message } from 'shell/components';
import { useAuth } from 'contexts/auth';
import courseService from 'services/course';

const OptionRowContainer = styled.div`
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    align-items: flex-start;
    margin-top: 40px;
    cursor: pointer;
`;

const OptionDetailContainer = styled.div`
    display: flex;
    width: 90%;
    flex-wrap: wrap;
    align-items: center;
`;

const InputContainer = styled.div`
    display: grid;
    align-items: center;
    margin: 0px;
    padding: 0px;
    grid-template-columns: 70px auto;
    gap: 8px;
`;

const StyledInput = styled(InputWithBorder)`
    margin: 0px 8px 0px 8px;
    padding: 0px;
    width: 70px;
    min-width: 70px;
    height: auto;

    & p {
        margin: 0px;
    }
`;

const StyledBody2 = styled(Body2)`
    margin: 0px 8px 0px 8px;
`;

const StyledRadioButton = styled(RadioButton)`
    margin: 0.7em 0;
`;

const StyledModal = styled(Modal)`
    width: 630px !important;

    & .ant-modal-content {
        min-width: unset;
    }
`;

const STEPS = ['team-formation', 'questionnaire-created', 'confirm-teams-modal', 'confirm-teams'];

const SmartGroupModal = ({ isOpen, onCancel, onNext }) => {
    const OPTIONS = ['set-groups', 'set-students-per-group'];
    const inputRef1 = useRef(null);
    const inputRef2 = useRef(null);
    const [mode, setMode] = useState(OPTIONS[0]);
    const [values, setValues] = useState({
        [OPTIONS[0]]: 0,
        [OPTIONS[1]]: 0,
    });

    const isDisabled = !(parseInt(values[mode]) > 0);

    useEffect(() => {
        if (mode === OPTIONS[0]) {
            inputRef1.current && inputRef1.current.focus();
        } else {
            inputRef2.current && inputRef2.current.focus();
        }
    }, [mode]);

    return (
        <StyledModal destroyOnClose visible={isOpen} onCancel={onCancel}>
            <Text variant={'h3'}>Create Teams Based on a Specific Set of Criteria</Text>
            <Text variant={'body1'}>I want to...</Text>
            <OptionRowContainer onClick={() => setMode(OPTIONS[0])}>
                <StyledRadioButton selected={mode === OPTIONS[0]} />
                <OptionDetailContainer>
                    <StyledBody2>Divide students into</StyledBody2>
                    <InputContainer>
                        <StyledInput
                            style={{ width: 70, margin: 0 }}
                            autoFocus
                            disabled={mode !== OPTIONS[0]}
                            inputRef={inputRef1}
                            value={values[OPTIONS[0]] || ''}
                            onChange={(value) => setValues((current) => ({ ...current, [OPTIONS[0]]: value }))}
                        />
                    </InputContainer>
                    <StyledBody2>groups</StyledBody2>
                </OptionDetailContainer>
            </OptionRowContainer>
            <OptionRowContainer onClick={() => setMode(OPTIONS[1])}>
                <StyledRadioButton selected={mode === OPTIONS[1]} />
                <OptionDetailContainer>
                    <StyledBody2>Divide students into groups of</StyledBody2>
                    <InputContainer>
                        <StyledInput
                            inputRef={inputRef2}
                            style={{ width: 70, margin: 0 }}
                            disabled={mode !== OPTIONS[1]}
                            value={values[OPTIONS[1]] || ''}
                            onChange={(value) => setValues((current) => ({ ...current, [OPTIONS[1]]: value }))}
                        />
                    </InputContainer>
                </OptionDetailContainer>
            </OptionRowContainer>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button color={'neutral'} variant={'tertiary'} onClick={onCancel}>
                    Cancel
                </Button>
                <Button disabled={isDisabled} onClick={() => onNext(mode, values[mode])}>
                    Next
                </Button>
            </div>
        </StyledModal>
    );
};

const TeamSetupType = ({ fieldKeys, options, courseData = {}, handleInputChange, review = false, onNextSmart }) => {
    const [option, setOption] = useState(options.CREATE_MY_OWN_GROUP);
    const [optionNumber, setOptionNumber] = useState({
        [options.DIVIDE_INTO_GROUP_NUM]: 0,
        [options.DIVIDE_INTO_STUDENT_NUM]: 0,
    });
    const [smartGroupModal, setSmartGroupModal] = useState({ isOpen: false });
    const inputRef = useRef([]);

    const handleFocus = (type) => {
        inputRef.current[type].disabled = false;
        inputRef.current[type].focus();
    };

    const handleSelectOptions = (key) => {
        setOption(key);
        let newStudentList = [];
        switch (key) {
            case options.DIVIDE_INTO_GROUP_NUM: {
                newStudentList = randomizeTeamByGroupNumber(optionNumber[key]);
                break;
            }
            case options.DIVIDE_INTO_STUDENT_NUM: {
                newStudentList = randomizeTeamByStudentNumber(optionNumber[key]);
                break;
            }
            case options.CREATE_SMART_GROUP: {
                setSmartGroupModal({ isOpen: true });
                return;
            }
            default:
                return;
        }
        handleFocus(key);
        if (newStudentList?.length <= 0) return;
        return handleInputChange(fieldKeys.STUDENT_LIST, newStudentList);
    };

    const randomizeTeamByGroupNumber = (number) => {
        if (number <= 0) return [];
        const numOfTeams = Number(number);
        const arrayNumbers = [];
        let setNumbers = new Set();
        while (arrayNumbers.length <= (courseData[fieldKeys.STUDENT_LIST] || []).length) {
            while (setNumbers.size < numOfTeams) {
                const randomNumber = Math.floor(Math.random() * numOfTeams + 1);
                setNumbers.add(randomNumber);
            }
            arrayNumbers.push(...setNumbers);
            setNumbers = new Set();
        }
        const changedData = courseData[fieldKeys.STUDENT_LIST].map((item, index) => {
            return { ...item, group: arrayNumbers[index] };
        });
        return changedData;
    };

    const randomizeTeamByStudentNumber = (number) => {
        if (number <= 0) return [];
        let groupNumber = 1;
        let memberCounter = 0;
        const changedData = courseData[fieldKeys.STUDENT_LIST].map((item, index) => {
            if (memberCounter >= number) {
                groupNumber += 1;
                memberCounter = 0;
            }
            memberCounter += 1;
            return { ...item, group: groupNumber };
        });
        return changedData;
    };

    const handleInputNumber = (key, value) => {
        const groupNumber = Number(value);

        if (!Number.isInteger(groupNumber) || 99 < groupNumber) {
            return message.error('Please enter a valid number.');
        }
        return setOptionNumber((prev) => ({ ...prev, [key]: value }));
    };

    const isSelected = (type) => {
        return option === options[type];
    };

    useEffect(() => {
        if (optionNumber[option] <= 99) {
            handleSelectOptions(option);
        }
    }, [optionNumber]);

    return (
        <>
            <CourseSetUpContainer>
                <HeaderContainer review={review}>
                    <H3>Create Teams</H3>
                    <Body1>
                        {' '}
                        Class size:
                        <span style={{ color: '#6347ff' }}>
                            {' '}
                            {courseData?.student_list?.length} student
                            {courseData?.student_list?.length === 1 ? '' : 's'}
                        </span>
                    </Body1>
                </HeaderContainer>
                <OptionRowContainer onClick={() => handleSelectOptions(options.DIVIDE_INTO_GROUP_NUM)}>
                    <StyledRadioButton selected={isSelected('DIVIDE_INTO_GROUP_NUM')} />
                    <OptionDetailContainer>
                        <StyledBody2> Randomize students into </StyledBody2>
                        <InputContainer>
                            <StyledInput
                                error={false}
                                errorMessage=''
                                placeholder=''
                                required={false}
                                title=''
                                disabled={!isSelected('DIVIDE_INTO_GROUP_NUM')}
                                inputRef={(e) => (inputRef.current[options.DIVIDE_INTO_GROUP_NUM] = e)}
                                value={
                                    optionNumber[options.DIVIDE_INTO_GROUP_NUM] === 0
                                        ? ''
                                        : optionNumber[options.DIVIDE_INTO_GROUP_NUM]
                                }
                                onChange={(value) => handleInputNumber(options.DIVIDE_INTO_GROUP_NUM, value)}
                                style={{ width: 70, margin: 0 }}
                            />
                        </InputContainer>
                        <StyledBody2> teams </StyledBody2>
                    </OptionDetailContainer>
                </OptionRowContainer>
                <OptionRowContainer onClick={() => handleSelectOptions(options.DIVIDE_INTO_STUDENT_NUM)}>
                    <StyledRadioButton selected={isSelected('DIVIDE_INTO_STUDENT_NUM')} />
                    <OptionDetailContainer>
                        <StyledBody2> Randomize students into teams of </StyledBody2>
                        <InputContainer>
                            <StyledInput
                                error={false}
                                errorMessage=''
                                placeholder=''
                                required={false}
                                disabled={!isSelected('DIVIDE_INTO_STUDENT_NUM')}
                                inputRef={(e) => (inputRef.current[options.DIVIDE_INTO_STUDENT_NUM] = e)}
                                title=''
                                value={
                                    optionNumber[options.DIVIDE_INTO_STUDENT_NUM] === 0
                                        ? ''
                                        : optionNumber[options.DIVIDE_INTO_STUDENT_NUM]
                                }
                                onChange={(value) => handleInputNumber(options.DIVIDE_INTO_STUDENT_NUM, value)}
                                style={{ width: 70 }}
                            />
                        </InputContainer>
                    </OptionDetailContainer>
                </OptionRowContainer>
                <OptionRowContainer onClick={() => setOption(options.CREATE_MY_OWN_GROUP)}>
                    <RadioButton selected={isSelected('CREATE_MY_OWN_GROUP')} />
                    <StyledBody2> Manually create my own teams </StyledBody2>
                </OptionRowContainer>
                <OptionRowContainer onClick={() => handleSelectOptions(options.CREATE_SMART_GROUP)}>
                    <RadioButton selected={isSelected('CREATE_SMART_GROUP')} />
                    <StyledBody2> Create teams based on a specific set of criteria </StyledBody2>
                </OptionRowContainer>
            </CourseSetUpContainer>
            <SmartGroupModal
                {...smartGroupModal}
                onCancel={() => {
                    setSmartGroupModal({ isOpen: false });
                    setOption(options.CREATE_MY_OWN_GROUP);
                }}
                onNext={(...args) => {
                    setSmartGroupModal({ isOpen: false });
                    onNextSmart(...args);
                }}
            />
        </>
    );
};

const CreateTeamSetUp = (props) => {
    const { auth } = useAuth();
    const [smartOptions, setSmartOptions] = useState({
        isOpen: props.open ? props.open : false,
    });
    const [loading, setLoading] = useState(false);
    const [groupFormation, setGroupFormation] = useState(null);
    const [surveys, setSurveys] = useState(null);
    const [step, setStep] = useState();

    useEffect(() => {
        (async () => {
            setLoading(true);
            const response = await courseService.listCourseGroupFormations(auth.course_id);
            const surveys = await courseService.listCourseGroupSurveys(auth.course_id);
            setSurveys(surveys);
            if (!response) {
                setStep(STEPS[0]);
                const now = new Date().getTime();
                setGroupFormation({
                    id: uuid.v4(),
                    active: true,
                    created_on: now,
                    modified_on: now,
                    owner_account_id: auth.owner_account_id,
                    course_id: auth.course_id,
                    criteria: [
                        {
                            id: 'time-zone',
                            name: 'Timezone',
                            custom_question: '',
                            weight: 3,
                            field_type: 'single-select',
                            mixture: 'homogeneous',
                            show: true,
                        },
                        {
                            id: 'country',
                            name: 'Country',
                            custom_question: '',
                            weight: 0,
                            field_type: 'single-select',
                            mixture: 'homogeneous',
                            show: true,
                        },
                        {
                            id: 'gender',
                            name: 'Pronouns',
                            custom_question: '',
                            weight: 0,
                            field_type: 'single-select',
                            mixture: 'homogeneous',
                            show: true,
                        },
                        {
                            id: 'english-proficiency',
                            name: 'English Proficiency',
                            custom_question: '',
                            weight: 0,
                            field_type: 'single-select',
                            mixture: 'homogeneous',
                            show: true,
                        },
                    ],
                    divider: { mode: 'set-groups', value: 0 },
                });
            } else {
                setGroupFormation({ ...response });
                if (!props.courseData.finished_course_setup) setStep(STEPS[1]);
            }
            setLoading(false);
        })();
    }, [auth.course_id, auth.owner_account_id]);

    if (smartOptions.isOpen)
        return (
            <SmartGroupFormation
                loading={loading}
                mode={groupFormation?.divider?.mode}
                fieldKeys={props.fieldKeys}
                modeValue={groupFormation?.divider?.value}
                surveys={surveys}
                setGroupFormation={(data) => setGroupFormation(data)}
                groupFormation={groupFormation}
                courseData={props.courseData}
                step={step}
                isEmptyTeamMode={props.isEmptyTeamMode}
                isNotEditable={surveys?.length > 0}
                setShowFooter={props.setShowFooter}
                setStep={(value) => setStep(value)}
                setSmartOptions={(mode, value) => setGroupFormation((prev) => ({ ...prev, divider: { mode, value } }))}
                handleInputChanges={props.handleInputChange}
                handleCourseSetUpNext={props.handleNext}
                handleCloseSmartGroupFormation={() => setSmartOptions({ isOpen: false })}
            />
        );

    return (
        <TeamSetupType
            {...props}
            onNextSmart={(mode, value) => {
                setSmartOptions({ isOpen: true, mode, value });
                setGroupFormation((prev) => ({ ...prev, divider: { mode, value } }));
                props.setShowFooter(false);
            }}
        />
    );
};

export default CreateTeamSetUp;
